import { FC, ReactNode, useEffect, useMemo, useState } from 'react';

import { MutationFetchPolicy } from '@apollo/client/core/watchQueryOptions';
import moment from 'moment';
import { FormProvider, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { twMerge } from 'tailwind-merge';

import { ErrorIcon } from 'assets/icons';
import { Button, Loader } from 'components';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import { If } from 'components/Generics';
import UploadScansModal from 'components/RejectTreatmentPlan/UploadScansModal';
import { VersionStatusComponent } from 'components/VersionStatusComponent/VersionStatusComponent';
import {
  ACCEPTANCE_STATUS,
  INTERNAL_DOCTOR,
  IN_PLANNING_STATUSES,
  KEEP_PLAN_OPENED,
  MOBILE_SCREEN_WIDTH,
  PATIENT,
  SKIP_INT_DOC_REJECTION_REASONS,
  STAGES,
  TPV_PAGE_CLICK_SOURCE,
  TP_ACTIONS_BLOCKED_STATUSES,
  TREATMENT_PLANNING_LAB,
  TREATMENT_STATUSES,
  USER_ROLES
} from 'constants/common';
import { PATIENT_INBOX_KEY } from 'constants/inbox';
import { TREATMENT_PLAN_VERSION_STATUS } from 'constants/options';
import { PATIENT_CARD_SIDE_PANEL_CONFIG } from 'constants/patientCard';
import { TREATMENT_TYPE } from 'constants/patientList';
import { PERMISSIONS } from 'constants/Permissions';
import { DATE_FORMAT } from 'constants/settings';
import { ReOpenPLan } from 'containers/ReOpenPlan/ReOpenPlan';
import { useTreatmentContext } from 'contexts/treatment.context';
import { CanAccess } from 'hoc/can-access/CanAccess';
import { useSubmitTPV } from 'services/hooks/TPVForm.hooks';
import {
  useGetTicketId,
  useUpdateTreatmentPlanVersion
} from 'services/hooks/TreatmentPlan.hooks';
import { useAttachSmarteeScans } from 'services/hooks/useAttachSmarteeScans';
import { GET_TREATMENT_PLANS } from 'services/query/TreatmentPlanReview.queries';
import {
  loginedUserDetails,
  selectedTicket,
  selectedChatAccordion
} from 'store/atoms';
import {
  isWarningClosed,
  showChatMessage,
  showMobileViewInbox,
  ticketList
} from 'store/atoms/inbox';
import { sideDrawerPreference } from 'store/atoms/patientListAtom';
import { getLoginedUserRoles } from 'store/selectors/auth';
import { FilesData } from 'types/Files.types';
import { PatientDetails } from 'types/PatientCard.types';
import { TreatmentPlanType, VersionDetails } from 'types/PatientList.types';
import { Ticket } from 'types/Tickets.types';
import {
  SortOrder,
  TOPIC_CODE,
  TOPIC_CONTEXT_TYPE,
  TREATMENT_PLAN_DISCUSSION
} from 'types/TreatmentPlanReview.types';
import { translate, useViewport } from 'utils';
import {
  checkIsExternal,
  getAttatchmentIPRValues,
  getCurrentTpvStatus
} from 'utils/common';
import { showToast } from 'utils/toast/toast.util';
import { buildNewScanDocumentsInput } from 'utils/treatmentDocument.utils';

export type UpdateNewScanFormValues = {
  newScans: FilesData[];
  resimulationInstructions: string;
};

interface Props {
  versionDetails?: VersionDetails;
  handleClick?: () => void;
  patientId?: string;
  treatmentPlan?: TreatmentPlanType;
  refetchTreatmentPlans?: () => void;
  hideReOpenPlan?: boolean;
  treatmentId?: string;
}

interface LabelInfoProps {
  label: ReactNode;
  value: ReactNode;
  className?: string;
  onClick?: () => void;
}

const LabelInfo: FC<LabelInfoProps> = ({
  label,
  value,
  className = '',
  onClick
}) => (
  <div className='flex items-center w-full mt-3'>
    <div className='font-normal text-GRAY3 md:text-sm xxs:text-xs md:h-6 w-1/3 md:w-3/12 mr-1'>
      {label}
    </div>
    <div
      className={twMerge('md:text-17px xxs:text-sm', className)}
      onClick={onClick}
      role='presentation'
    >
      {value || '-'}
    </div>
  </div>
);

const resimulationConfirmModalTitle = (
  <span className='flex items-start gap-3 translate-y-2 '>
    <ErrorIcon className='fill-current text-DARK_BORDER_RED w-5 h-5 shrink-0' />
    <span className='text-ONYX leading-6 font-semibold text-xl'>
      {translate('update_new_scan_confirm.title')}
    </span>
  </span>
);

export const TreatmentVersionCard: FC<Props> = ({
  versionDetails,
  handleClick,
  patientId,
  treatmentPlan,
  refetchTreatmentPlans,
  hideReOpenPlan,
  treatmentId
}) => {
  const isToothsiAdmin = useRecoilValue(getLoginedUserRoles).includes(
    USER_ROLES.TOOTHSI_ADMIN
  );
  const setSelectedTicket = useSetRecoilState(selectedTicket);
  const setShowMobileViewInbox = useSetRecoilState(showMobileViewInbox);
  const setSelectedAccordion = useSetRecoilState(selectedChatAccordion);
  const ticketsList = useRecoilValue(ticketList);
  const userDetails = useRecoilValue(loginedUserDetails);
  const setSidepanelPreference = useSetRecoilState(sideDrawerPreference);
  const setWarningClosed = useSetRecoilState(isWarningClosed);
  const setShowChat = useSetRecoilState(showChatMessage);
  const { patientDetails } = useTreatmentContext();
  const opportunityType =
    patientDetails && 'type' in patientDetails
      ? (patientDetails as PatientDetails).type
      : undefined;
  const isToothsiVersion =
    versionDetails?.treatmentPlanningLab === TREATMENT_PLANNING_LAB.TOOTHSI;
  const isSmarteeVersion =
    versionDetails?.treatmentPlanningLab === TREATMENT_PLANNING_LAB.SMARTEE;
  const { width } = useViewport();
  const isMobile = width < MOBILE_SCREEN_WIDTH;
  const currentTpvStatus = useMemo(() => {
    return getCurrentTpvStatus(versionDetails);
  }, [versionDetails]);

  const { attachSmarteeScans, isAttaching: isAttachingSmarteeScans } =
    useAttachSmarteeScans(treatmentId, isSmarteeVersion);

  const [showUpdateScanModal, setShowUpdateScanModal] = useState(false);
  const [showResimulationConfirmModal, setShowResimulationConfirmModal] =
    useState(false);
  const updateScanFormMethods = useForm<UpdateNewScanFormValues>({
    defaultValues: {
      newScans: [],
      resimulationInstructions: ''
    }
  });
  const { reset: resetUpdateScanForm, getValues } = updateScanFormMethods;

  const [submitTPV, { loading: submitResimLoading }] = useSubmitTPV();

  const [updateTreatmentPlanVersion, { loading: updateRejectLoading }] =
    useUpdateTreatmentPlanVersion({
      fetchPolicy: 'no-cache' as MutationFetchPolicy,
      refetchQueries: [GET_TREATMENT_PLANS],
      onCompleted: () => {
        const { newScans, resimulationInstructions } = getValues();
        const documentsInput = buildNewScanDocumentsInput(newScans);
        if (
          !treatmentId ||
          !versionDetails?.id ||
          !opportunityType ||
          isSmarteeVersion
        ) {
          setShowResimulationConfirmModal(false);
          resetUpdateScanForm({
            newScans: [],
            resimulationInstructions: ''
          });
          return;
        }
        submitTPV({
          variables: {
            input: {
              treatmentId,
              resimParentId: versionDetails.id,
              // TPV API does not accept AFTERSALES as treatmentType; submit as BRACES instead.
              treatmentType:
                opportunityType === TREATMENT_TYPE.AFTERSALES
                  ? TREATMENT_TYPE.BRACES
                  : opportunityType,
              prescription: {
                specialNotes: resimulationInstructions,
                type: versionDetails.type
              },
              ...(isToothsiVersion && documentsInput?.length
                ? { documents: documentsInput }
                : {})
            }
          },
          refetchQueries: [GET_TREATMENT_PLANS],
          onCompleted: () => {
            showToast(translate('tpv.resim_submitted_success'), true);
            setShowResimulationConfirmModal(false);
            resetUpdateScanForm({
              newScans: [],
              resimulationInstructions: ''
            });
            refetchTreatmentPlans?.();
          }
        });
      }
    });

  const openUpdateScanModal = () => {
    resetUpdateScanForm({
      newScans: [],
      resimulationInstructions: ''
    });
    setShowUpdateScanModal(true);
  };

  const closeUpdateScanModal = () => {
    setShowUpdateScanModal(false);
    resetUpdateScanForm({
      newScans: [],
      resimulationInstructions: ''
    });
  };

  const handleUploadScansSubmit = () => {
    if (!opportunityType) {
      showToast(
        translate('update_new_scan_confirm.missing_opportunity_type'),
        false
      );
      return;
    }
    setShowUpdateScanModal(false);
    setShowResimulationConfirmModal(true);
  };

  const handleConfirmResimulationRequest = async () => {
    if (!treatmentId || !versionDetails?.id || !opportunityType) {
      return;
    }
    const { resimulationInstructions } = getValues();

    if (isSmarteeVersion) {
      const isAttached = await attachSmarteeScans(getValues().newScans);
      if (!isAttached) return;
    }

    updateTreatmentPlanVersion({
      variables: {
        id: versionDetails.id,
        input: {
          acceptanceStatus: ACCEPTANCE_STATUS.REJECTED,
          rejectionInfo: {
            reason: SKIP_INT_DOC_REJECTION_REASONS.PLAN_TO_BE_RESIMULATED,
            comments: resimulationInstructions,
            // this flow is currently only smartee and toothsi only for new scans (ZEN-14826)
            rejectedBy: PATIENT
          }
        }
      }
    });
  };

  const {
    data: tickets,
    loading,
    refetch
  } = useGetTicketId({
    variables: {
      params: {
        patientId,
        paginationInfo: {
          limit: 1,
          offset: 0,
          sortBy: 'createdAt',
          sortOrder: SortOrder.DESC
        },
        topicCode: [
          TOPIC_CODE.DISCUSSION_REQUIRED,
          TOPIC_CODE.REQUEST_NEW_VERSION,
          TOPIC_CODE.CASE_WITH_INT_DOC,
          TOPIC_CODE.CASE_OPS_ISSUE
        ],
        contextType: TOPIC_CONTEXT_TYPE.TPV,
        contextValueId: versionDetails?.id
      },
      loginedUserId: userDetails?.id
    },
    fetchPolicy: 'no-cache',
    skip: isToothsiAdmin
  });

  useEffect(() => {
    if (ticketsList?.[0]?.topicInfo?.topicCode === TREATMENT_PLAN_DISCUSSION) {
      refetch();
      refetchTreatmentPlans?.();
    }
  }, [ticketsList?.length]);

  const updateSidepanelPreference = (view: string, isOpen: boolean) => {
    if (patientId) {
      setSidepanelPreference({
        view,
        isOpen
      });
    }
  };

  const openChat = (ticketDetails: Ticket) => {
    setShowChat(true);
    if (!isMobile) {
      setWarningClosed(true);
      updateSidepanelPreference(PATIENT_CARD_SIDE_PANEL_CONFIG.INBOX.key, true);
    }
    const selectedTicketIndex = ticketsList.findIndex(
      (ticket) => ticket.id === ticketDetails.id
    );
    selectedTicketIndex !== -1
      ? setSelectedTicket(ticketsList[selectedTicketIndex])
      : setSelectedTicket(ticketDetails);
    setSelectedAccordion([PATIENT_INBOX_KEY.MY_CHAT]);
    isMobile && setShowMobileViewInbox(true);
  };
  const labelInfoConfig = [
    {
      key: 1,
      label: translate('date_created.date_created'),
      value: moment(versionDetails?.createdAt)?.format(DATE_FORMAT)
    },
    {
      key: 4,
      label: translate('remarks.remarks'),
      value: tickets?.getTickets?.tickets?.[0]?.id
        ? `Conversation #${tickets?.getTickets?.tickets?.[0]?.id}`
        : '',
      className: tickets?.getTickets?.tickets?.[0]?.id
        ? 'underline text-PRIMARY cursor-pointer'
        : '',
      onClick: () =>
        tickets?.getTickets?.tickets?.[0]?.id
          ? openChat(tickets.getTickets.tickets[0])
          : null
    },
    {
      key: 6,
      label: translate('status.status'),
      value: versionDetails?.acceptanceStatus && (
        <>
          <VersionStatusComponent status={versionDetails?.acceptanceStatus} />
          <If
            condition={
              !!versionDetails?.doctor3dViewerUrl &&
              !hideReOpenPlan &&
              currentTpvStatus === TREATMENT_PLAN_VERSION_STATUS.REJECTED &&
              treatmentPlan?.treatment?.stage ===
                STAGES.TREATMENT_PLAN_REVIEW &&
              versionDetails?.rejectionInfo?.rejectedBy !== INTERNAL_DOCTOR &&
              versionDetails?.rejectionInfo?.rejectedBy !== PATIENT &&
              versionDetails?.rejectionInfo?.rejectedBy !== null &&
              (treatmentPlan?.treatment?.status !==
                TREATMENT_STATUSES.PATIENT_ACCEPTED_PENDING_FITTING_APPOINTMENT ||
                treatmentPlan?.treatment?.status !==
                  TREATMENT_STATUSES.FITTING_APPOINTMENT_BOOKED)
            }
          >
            <div className='flex flex-col md:block xxs:hidden'>
              <CanAccess
                permission={PERMISSIONS.REOPEN_TPV}
                yes={
                  <ReOpenPLan
                    buttonType={KEEP_PLAN_OPENED}
                    planVersionId={versionDetails?.id}
                    planVersionName={versionDetails.name}
                    treatmentPlanName={treatmentPlan?.label}
                    treatmentPlanId={treatmentPlan?.id}
                    patientId={patientId || ''}
                    disabled={TP_ACTIONS_BLOCKED_STATUSES.includes(
                      treatmentPlan?.treatment?.status || ''
                    )}
                  />
                }
              />
            </div>
          </If>
        </>
      )
    }
  ];
  const isOpenPlanVisible = (status?: TREATMENT_PLAN_VERSION_STATUS) => {
    return (
      (status !== TREATMENT_PLAN_VERSION_STATUS.INT_DOC_TPV_CHECK &&
        !IN_PLANNING_STATUSES.includes(
          status as TREATMENT_PLAN_VERSION_STATUS
        )) ||
      (!checkIsExternal(userDetails.groups) &&
        status === TREATMENT_PLAN_VERSION_STATUS.CASE_WITH_INT_DOC)
    );
  };
  const renderOpenPlan = () => {
    return (
      <div className='flex justify-end w-full'>
        <If
          condition={
            (isToothsiVersion || isSmarteeVersion) &&
            versionDetails.acceptanceStatus === ACCEPTANCE_STATUS.ACCEPTED &&
            (patientDetails as PatientDetails).status ===
              TREATMENT_STATUSES.PATIENT_ACCEPTED_PENDING_FITTING_APPOINTMENT &&
            (patientDetails as PatientDetails).type !== TREATMENT_TYPE.BRACES &&
            !(patientDetails as PatientDetails).alignerProductionInfo
              ?.productionRecord
          }
        >
          <div className='flex justify-end w-fit'>
            <Button
              type='button'
              className='text-sm rounded-32px border border-DARK_BORDER_RED py-1 px-4 md:mr-2 text-DARK_BORDER_RED'
              onClick={() => {
                if (!treatmentId) return;
                openUpdateScanModal();
              }}
            >
              {translate('button.update_new_scan')}
            </Button>
          </div>
        </If>
        <If condition={isOpenPlanVisible(currentTpvStatus)}>
          <div className='flex justify-end w-fit'>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link
              to={`/tpv/${versionDetails?.id}?tid=${treatmentId}&tpid=${treatmentPlan?.id}&clickSource=${TPV_PAGE_CLICK_SOURCE.PATIENT_CARD}`}
              className='text-sm rounded-32px border border-DEFAULT_TEXT py-1 px-4 md:mr-2'
              rel='noreferrer'
              onClick={handleClick}
            >
              {translate('chips_label.open_plan')}
            </Link>
          </div>
        </If>
      </div>
    );
  };
  if (loading || isAttachingSmarteeScans) return <Loader />;
  return (
    <FormProvider {...updateScanFormMethods}>
      <div className='md:flex md:space-y-0  xxs:space-y-4 md:space-x-1 justify-between p-4 bg-WHITE rounded-lg flex flex-col'>
        <div className='w-full flex justify-between items-center pb-3 border-b-1 border-solid border-DEFAULT_TEXT'>
          <div className=' flex flex-col md:flex-row w-full'>
            <span className='block mr-2'>{`${versionDetails?.name}: `} </span>
            <span>
              {getAttatchmentIPRValues(
                versionDetails?.ipr,
                versionDetails?.attachments
              )}
            </span>
          </div>
          {renderOpenPlan()}
        </div>
        <div>
          {labelInfoConfig.map((tpvCardItem) => {
            if (
              tpvCardItem.label !== translate('remarks.remarks') ||
              tpvCardItem.value !== ''
            )
              return (
                <LabelInfo
                  key={tpvCardItem.key}
                  label={tpvCardItem.label}
                  value={tpvCardItem.value}
                  className={tpvCardItem.className}
                  onClick={tpvCardItem.onClick}
                />
              );
          })}
        </div>
      </div>
      <If condition={!!treatmentId && showUpdateScanModal}>
        <UploadScansModal
          isOpen={showUpdateScanModal}
          onClose={closeUpdateScanModal}
          treatmentId={treatmentId || ''}
          showResimulationInstructions
          onSubmit={handleUploadScansSubmit}
          closeAfterSubmit={false}
          title={translate('request.resim')}
        />
      </If>
      <ConfirmationModal
        open={showResimulationConfirmModal}
        onCancel={() => setShowResimulationConfirmModal(false)}
        onConfirm={handleConfirmResimulationRequest}
        title={resimulationConfirmModalTitle}
        message={translate('update_new_scan_confirm.body')}
        confirmLabel={translate('update_new_scan_confirm.confirm_request')}
        disableConfirmButton={
          submitResimLoading || updateRejectLoading || isAttachingSmarteeScans
        }
        confirmButtonClassName='bg-DARK_BORDER_RED text-WHITE'
        hideMessageIcon={true}
        showCloseIconHeader={true}
        className='px-6 pt-6 pb-3.5'
      />
    </FormProvider>
  );
};
